import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import * as microsoftTeams from "@microsoft/teams-js";
import { setDefaultRight, setGlobalPermissions, usePermissionsSelector } from "../../redux/reducers/Permissions/PermissionsReducer";
import { useAppConfigurationSelector } from "../../redux/reducers/AppConfigurationReducer/AppConfigurationReducer";
import { useConfigurationSelector } from "../../redux/reducers/ConfigurationReducer/ConfigurationReducer";
import { useMsTeamsSelector } from "../../redux/reducers/MicrosoftTeamsReducer/MicrosoftTeamsReducer";
import { openModal, useModalSelector } from "../../redux/reducers/ModalReducer/ModalReducer";
import { usePremiumSelector } from "../../redux/reducers/PremiumReducer/PremiumReducer";
import { GlobalPermsString, PermsTable, Role } from "../../interfaces/Permissions";
import { Modal } from "../../interfaces/Modal";
import { CompareModule } from "../../modules/Compare.module";
import { ObjectModule } from "../../modules/Object.module";
import { createUserPerm, getDefaultPermissionsByProjectId, getUsersPermissionsByProjectId, patchDefaultPerm, patchUserPerm, postGlobalPerms, } from "../../apis/Permissions/PermissionsApi";
import { getAdminCenterData } from "../../apis/AdminCenter/adminCenterApi";
import { PARTICIPANT_NO_ID } from "../../const/const";
import { prioritizeInAppPurchase } from "../../utils/prioritizeInAppPurchase";
import { buildDefaultRight, buildRight, getConfigPerms } from "./Permissions.utils";
/** Members shown per page. */
export const permissionsPageSize = 20;
/** How long the "saved" confirmation stays. */
const savedNoticeDuration = 4000;
const searchDebounce = 400;
const modeOf = (globalPermissions) => Object.values(GlobalPermsString).find((mode) => globalPermissions?.[mode]) ?? GlobalPermsString.isEveryone;
const clone = (value) => JSON.parse(JSON.stringify(value));
export const usePermissionsLogic = () => {
    const { appPathName, appId, appName, from } = useAppConfigurationSelector("appPathName", "appId", "appName", "from");
    const { defaultRight, globalPermissions, userPerm } = usePermissionsSelector("defaultRight", "globalPermissions", "userPerm");
    const { isOpen } = useModalSelector("isOpen");
    const { data } = useConfigurationSelector("data");
    const { tenantId, userId } = useMsTeamsSelector("tenantId", "userId");
    const { isPremium, isPlatinum } = usePremiumSelector("isPremium", "isPlatinum");
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const permsColumns = useMemo(() => getConfigPerms(from), [from]);
    const [state, setState] = useState({
        defaultRight: buildDefaultRight(from, { ...defaultRight }),
        globalPermissions: { isEveryone: globalPermissions.isEveryone, isCreator: globalPermissions.isCreator, isCustom: globalPermissions.isCustom },
    });
    const savedState = useRef(clone(state));
    const [users, setUsers] = useState([]);
    const savedUsers = useRef([]);
    // Members changed since the last save, whatever page they were changed on.
    const changedUsers = useRef(new Map());
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchInput, setSearchInput] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);
    const [saved, setSaved] = useState(false);
    const [leaveAfterDiscard, setLeaveAfterDiscard] = useState(false);
    const [roleChangeIndex, setRoleChangeIndex] = useState();
    const fetchId = useRef(0);
    const isOwner = userPerm?.role === Role.creator;
    const readOnly = !isOwner;
    const needsUpgrade = !isPlatinum;
    const modeLocked = readOnly || (prioritizeInAppPurchase(from) && !isPlatinum);
    const mode = modeOf(state.globalPermissions);
    const isCustom = mode === GlobalPermsString.isCustom;
    const totalPage = Math.max(1, Math.ceil(total / permissionsPageSize));
    const isDirty = useMemo(() => !loading && (!CompareModule.areObjectsEqual(state, savedState.current) || !CompareModule.areObjectsEqual(users, savedUsers.current)), [state, users, loading]);
    /** Licence of each member, asked to Admin Center; the current user's is already known. */
    const withLicences = async (pageUsers) => Promise.all(pageUsers.map(async (user) => {
        if (user.id === userId || process.env.REACT_APP_ISPLATINUM)
            return { ...user, isPlatinum, isPremium };
        try {
            const licence = await getAdminCenterData(tenantId, user.id, data.adminCenterAppId, data.adminCenterBaseUrl);
            return { ...user, isPlatinum: licence.isUserPlatinum, isPremium: licence.isUserPremium };
        }
        catch {
            return { ...user, isPlatinum: false, isPremium: false };
        }
    }));
    useEffect(() => {
        if (!appId)
            return;
        const id = ++fetchId.current;
        setLoading(true);
        (async () => {
            try {
                const page = await getUsersPermissionsByProjectId(appId, currentPage - 1, searchValue, permissionsPageSize);
                const pageUsers = await withLicences(page?.users ?? []);
                // A slower answer to an older search or page must not replace the current one.
                if (id !== fetchId.current)
                    return;
                // Changes made before a page change stay visible when coming back to that page.
                const shown = pageUsers.map((user) => changedUsers.current.get(user.id) ?? user);
                savedUsers.current = clone(pageUsers);
                setUsers(clone(shown));
                setTotal(page?.total ?? 0);
            }
            catch {
                if (id !== fetchId.current)
                    return;
                savedUsers.current = [];
                setUsers([]);
                setTotal(0);
            }
            finally {
                if (id === fetchId.current)
                    setLoading(false);
            }
        })();
    }, [appId, currentPage, searchValue, isPlatinum, isPremium, userId]);
    useEffect(() => {
        if (!appId)
            return;
        getDefaultPermissionsByProjectId(appId)
            .then((serverDefaultRight) => {
            if (!serverDefaultRight)
                return;
            savedState.current = { ...savedState.current, defaultRight: serverDefaultRight };
            setState((previous) => ({ ...previous, defaultRight: serverDefaultRight }));
        })
            .catch(() => undefined);
    }, [appId]);
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchInput === searchValue)
                return;
            setCurrentPage(1);
            setSearchValue(searchInput);
        }, searchDebounce);
        return () => clearTimeout(timer);
    }, [searchInput]);
    useEffect(() => {
        if (!saved)
            return;
        const timer = setTimeout(() => setSaved(false), savedNoticeDuration);
        return () => clearTimeout(timer);
    }, [saved]);
    const updateUser = (index, update) => {
        setSaved(false);
        setUsers((previous) => previous.map((user, i) => {
            if (i !== index)
                return user;
            const next = update(user);
            changedUsers.current.set(next.id, next);
            return next;
        }));
    };
    const selectMode = (selected) => {
        if (modeLocked || selected === mode)
            return;
        const nextGlobal = { isEveryone: false, isCreator: false, isCustom: false, [selected]: true };
        // Owners only: members lose every right. Everyone and custom start from every right.
        const nextDefault = buildRight(from, selected !== GlobalPermsString.isCreator);
        setSaved(false);
        setState({ globalPermissions: nextGlobal, defaultRight: nextDefault });
        // Every member except the owners takes the rights of the chosen mode.
        setUsers((previous) => previous.map((user) => {
            if (user.role === Role.creator)
                return user;
            const perm = { ...(user.perm ?? {}) };
            permsColumns.forEach((column) => (perm[column] = !!nextDefault[column]));
            const next = { ...user, perm };
            changedUsers.current.set(next.id, next);
            return next;
        }));
    };
    const canEditUser = (user) => isCustom && isOwner && !!isPlatinum && user.role !== Role.creator && !!user.isPlatinum;
    const togglePerm = (index, perm) => {
        const user = users[index];
        if (!user || !canEditUser(user))
            return;
        updateUser(index, (u) => ({ ...u, perm: { ...u.perm, [perm]: !u.perm?.[perm] } }));
    };
    const canPromote = (user) => isOwner && user.role !== Role.creator && !!user.isPlatinum;
    const askPromote = (index) => {
        if (isOpen)
            return;
        setRoleChangeIndex(index);
        dispatch(openModal(Modal.PERMISSIONS_ROLE_CHANGE));
    };
    /** Owners have every right; the role cannot be taken back from the page. */
    const promote = (index) => {
        updateUser(index, (u) => ({
            ...u,
            role: Role.creator,
            perm: Object.fromEntries(Object.keys(u.perm ?? {}).map((key) => [key, true])),
        }));
    };
    const pickMembers = async () => {
        if (readOnly || (prioritizeInAppPurchase(from) && !isPlatinum))
            return;
        let picked = [];
        try {
            picked = await microsoftTeams.people.selectPeople({ singleSelect: false, title: "", openOrgWideSearchInChatOrChannel: true });
        }
        catch {
            return;
        }
        const added = [];
        for (const person of picked) {
            if (users.some((user) => user.id === person.objectId))
                continue;
            let licence = { isUserPlatinum: false, isUserPremium: false };
            try {
                licence = await getAdminCenterData(tenantId, person.objectId, data.adminCenterAppId, data.adminCenterBaseUrl);
            }
            catch {
                // Without its licence the member is added as a free one, whose rights cannot be customized.
            }
            const member = {
                id: !person.objectId ? PARTICIPANT_NO_ID : person.objectId,
                name: person.displayName ?? person.email ?? "",
                upn: person.email ?? "",
                role: Role.member,
                perm: ObjectModule.deepClone(state.defaultRight),
                isPlatinum: (process.env.REACT_APP_ISPLATINUM ?? licence.isUserPlatinum),
                isPremium: (process.env.REACT_APP_ISPREMIUM ?? licence.isUserPremium),
            };
            try {
                // Adding is saved at once: the member exists on the server from now on.
                await createUserPerm(appId, member);
                added.push(member);
            }
            catch {
                // A member the server refused is not shown.
            }
        }
        if (added.length === 0)
            return;
        savedUsers.current = [...savedUsers.current, ...clone(added)];
        setUsers((previous) => [...previous, ...added]);
        setTotal((previous) => previous + added.length);
    };
    const save = async () => {
        if (!isDirty || saving || readOnly)
            return;
        setSaving(true);
        setSaveFailed(false);
        const nextDefault = buildDefaultRight(from, { ...state.defaultRight });
        try {
            await patchDefaultPerm(appId, nextDefault);
            await postGlobalPerms(appId, state.globalPermissions);
            const sharesSync = permsColumns.includes(PermsTable.AllowShareSynchro);
            const changed = [];
            changedUsers.current.forEach((user) => changed.push(user));
            await Promise.all(changed.map((user) => {
                // A member who may no longer share stops sharing his synchronized calendars.
                const synchros = sharesSync && !user.perm?.allowShareSynchro && user.synchros ? user.synchros.map((s) => ({ ...s, sharing: false })) : user.synchros;
                return patchUserPerm(appId, { ...user, synchros });
            }));
        }
        catch {
            setSaveFailed(true);
            setSaving(false);
            return;
        }
        dispatch(setDefaultRight(nextDefault));
        dispatch(setGlobalPermissions(state.globalPermissions));
        changedUsers.current.clear();
        savedState.current = clone({ ...state, defaultRight: nextDefault });
        savedUsers.current = clone(users);
        setState((previous) => ({ ...previous, defaultRight: nextDefault }));
        setSaving(false);
        setSaved(true);
    };
    const discard = () => {
        changedUsers.current.clear();
        setState(clone(savedState.current));
        setUsers(clone(savedUsers.current));
        setSaveFailed(false);
    };
    const returnToApp = () => {
        if (!appPathName)
            return;
        navigate(`/${appPathName}/` + appId);
    };
    /** Leaving with unsaved changes asks first; the same dialog guards the discard button. */
    const askDiscard = (thenLeave) => {
        if (isOpen)
            return;
        setLeaveAfterDiscard(thenLeave);
        dispatch(openModal(Modal.PERMISSIONS_CANCEL_UNSAVE_CHANGE));
    };
    const goBack = () => {
        if (saving)
            return;
        if (isDirty)
            askDiscard(true);
        else
            returnToApp();
    };
    const changePage = (page) => {
        // Paging reloads the members from the server: changes are saved or discarded first.
        if (isDirty || page < 1 || page > totalPage)
            return;
        setCurrentPage(page);
    };
    const openPremium = useCallback(() => {
        if (prioritizeInAppPurchase(from))
            microsoftTeams.monetization.openPurchaseExperience();
        else
            dispatch(openModal(Modal.PREMIUM));
    }, [from]);
    return {
        from,
        appName,
        userId,
        permsColumns,
        mode,
        isCustom,
        modeLocked,
        readOnly,
        needsUpgrade,
        isPremium,
        isPlatinum,
        users,
        total,
        loading,
        currentPage,
        totalPage,
        searchInput,
        setSearchInput,
        searchValue,
        isDirty,
        saving,
        saveFailed,
        saved,
        isOpen,
        leaveAfterDiscard,
        roleChangeIndex,
        selectMode,
        canEditUser,
        togglePerm,
        canPromote,
        askPromote,
        promote,
        pickMembers,
        canAddMembers: !readOnly && !(prioritizeInAppPurchase(from) && !isPlatinum),
        save,
        discard,
        askDiscard,
        goBack,
        returnToApp,
        changePage,
        openPremium,
    };
};
